import {
  CompanyInvoice,
  CompanyInvoiceMode,
  CompanyInvoiceType,
  CompanyInvoiceWithRelations,
  JobType,
} from '@utilities-one/api-sdk';

import { isSplicingJobType } from '@/config/jobs';

interface InvoiceForSplicingCheck {
  items?: Array<{ jobSpecification?: { jobType?: string | null } | null }> | null;
}

export const isCreditMemo = (invoice: Pick<CompanyInvoice, 'mode'>): boolean =>
  invoice.mode === CompanyInvoiceMode.creditMemo;

/**
 * UG-4958: a standalone memo (no parent invoice) has a manual amount and no line items. A damage
 * credit memo has no parent invoice either, but it carries the repair job items, so "standalone"
 * is decided by the items, not by the missing parent.
 */
export const isStandaloneCreditMemo = (
  invoice: Pick<CompanyInvoice, 'mode' | 'parentId'> & { items?: unknown[] | null }
): boolean => isCreditMemo(invoice) && !invoice.parentId && !invoice.items?.length;

/** Category id/label for the invoice grid: Invoice | Credit Memo | U1 Cost Credit Memo. */
export const companyInvoiceCategoryNames = {
  invoice: 'Invoice',
  creditMemo: 'Credit Memo',
  u1CostCreditMemo: 'U1 Cost Credit Memo',
} as const;

export type CompanyInvoiceCategory = keyof typeof companyInvoiceCategoryNames;

export const getInvoiceCategory = (
  invoice: Pick<CompanyInvoice, 'mode'> & { isU1Cost?: boolean | null }
): CompanyInvoiceCategory => {
  if (invoice.mode !== CompanyInvoiceMode.creditMemo) return 'invoice';
  return invoice.isU1Cost ? 'u1CostCreditMemo' : 'creditMemo';
};

export const getInvoiceCategoryLabel = (
  invoice: Pick<CompanyInvoice, 'mode'> & { isU1Cost?: boolean | null }
): string => companyInvoiceCategoryNames[getInvoiceCategory(invoice)];

export const hasParDiems = (invoice: Pick<CompanyInvoiceWithRelations, '_count'>): boolean => {
  return (invoice._count?.perDiemInvoiceItems ?? 0) > 0;
};

export const isMSAInvoice = (invoice: Pick<CompanyInvoiceWithRelations, 'type'>): boolean => {
  return invoice.type === CompanyInvoiceType.msa;
};

export const isVendorInvoice = (invoice: Pick<CompanyInvoiceWithRelations, 'type'>): boolean => {
  return invoice.type === CompanyInvoiceType.vendor;
};

/** UG-4831: W-2 invoices bill timesheet hours, not production job items. */
export const isW2Invoice = (invoice: Pick<CompanyInvoiceWithRelations, 'type'>): boolean => {
  return invoice.type === CompanyInvoiceType.w2;
};

export const isSplicingInvoice = (invoice: InvoiceForSplicingCheck): boolean => {
  const jobType = invoice.items?.[0]?.jobSpecification?.jobType;
  return !!jobType && isSplicingJobType(jobType as JobType);
};
